<template>
  <div>
    <b-overlay
      :show="groupsResponses('groupsList') && groupsEmpty"
      spinner-type="border"
      variant="light"
      spinner-variant="primary"
      opacity="1"
      rounded>
      <div class="d-flex align-items-end pt-2 pb-6">
        <div class="d-flex flex-column">
          <h1 class="h5 mb-2">
            Groups
          </h1>
          <b-breadcrumb class="breadcrumb-transparent breadcrumb-line p-0 m-0">
            <b-breadcrumb-item :to="{ name: 'Dashboard' }">
              <i class="fa fa-home fa-sm mr-2"></i>
              Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Settings</b-breadcrumb-item>
            <b-breadcrumb-item active>Groups</b-breadcrumb-item>
          </b-breadcrumb>
        </div>
        <div class="ml-auto">
          <b-button
            size="sm"
            v-wave
            variant="primary"
            class="align-middle btn-shadow"
            :disabled="groupsResponses('manageGroup')"
            @click="unselectGroup(), showModal('manageModal')">
            <i class="fa fa-plus fa-sm p-0 pr-sm-2"></i>
            <span class="d-none d-sm-inline">Add New Group</span>
          </b-button>
        </div>
      </div>

      <b-card
        no-body
        class="card-custom card-border">

        <b-card-body>
          <b-table
            :items="groupsList"
            :fields="fields"
            :class="{ 'table-hover' : !groupsEmpty }"
            stacked="sm"
            head-variant=""
            show-empty
            responsive
            class="mb-0">
            <template #cell(order)="data">
              <span class="small font-weight-light">{{ data.index + 1 }}</span>
            </template>
            <template #cell(servers)="data">
              <div class="my-2">{{ data.item.servers.active }}</div>
              <div class="my-1 font-size-xs opacity-75">({{ data.item.servers.count }} total)</div>
            </template>
            <template #cell(edit)="data">
              <b-button
                size="sm"
                v-wave
                variant="outline-primary"
                class="mr-4 align-middle"
                :disabled="groupsResponses('manageGroup')"
                @click="selectGroup(data.item), showModal('manageModal')">
                <i class="fa fa-pen fa-sm"></i>
                <span>Edit</span>
              </b-button>
              <b-button
                :id="`deleteButton-${data.item._id}`"
                v-wave
                size="sm"
                variant="outline-primary"
                class="btn-icon"
                :disabled="groupsResponses('deleteGroup')"
                @click="selectGroup(data.item), showDeletePopover(data.item._id)">
                <i class="fa fa-trash fa-sm"></i>
              </b-button>
            </template>
            <template #empty="scope">
              <no-data-found />
            </template>
          </b-table>
        </b-card-body>
      </b-card>
    </b-overlay>

    <b-popover
      v-for="group in groupsList"
      :key="`popover-${group._id}`"
      :target="`deleteButton-${group._id}`"
      :ref="`popover-${group._id}`"
      triggers="manual blur"
      placement="left">
      <p>Are you sure you want to delete «<span class="font-weight-bolder">{{ group.name }}</span>» group?</p>
      <div class="d-flex w-100">
        <b-button
          @click="hideDeletePopover(group._id)"
          variant="outline-primary"
          size="sm">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          size="sm"
          class="ml-4 text-center justify-content-center flex-grow-1"
          @click="confirmDelete(), hideDeletePopover(group._id)">
          <i class="fa fa-check fa-sm"></i>
          <span>Yes, delete</span>
        </b-button>
      </div>
    </b-popover>

    <b-modal
      ref="manageModal"
      centered
      size="md"
      :title="groupsModalTitle"
      hide-header-close
      header-class="py-4 border-bottom-0"
      body-class="pt-4"
      hide-footer
      @hidden="unselectGroup(), clearErrors()">
      <b-form @submit.stop.prevent>
        <b-form-group
          id="name-group"
          label="Name"
          label-for="name-input">
          <b-form-input
            id="name-input"
            name="name-input"
            v-model="selectedGroup.name"
            :state="getValidationState('name')"
            aria-describedby="name-feedback"
            placeholder="Premium" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageGroupError('name')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <b-form-group
          id="code-group"
          label="Code"
          label-for="code-input">
          <b-form-input
            id="code-input"
            name="code-input"
            v-model="selectedGroup.code"
            :state="getValidationState('code')"
            :disabled="selectedGroupsExist"
            aria-describedby="code-feedback"
            placeholder="TARIFF_PREMIUM" />
          <b-form-invalid-feedback
            v-for="(error, index) in manageGroupError('code')"
            :key="`${error.field}-${index}`">
            {{ error.message }}
          </b-form-invalid-feedback>
        </b-form-group>

        <div class="d-flex w-100 pt-2">
          <b-button
            variant="outline-secondary"
            size="md"
            @click="hideModal('manageModal')">
            Cancel
          </b-button>
          <b-button
            type="submit"
            v-wave
            variant="primary"
            size="md"
            class="ml-auto"
            :disabled="groupsResponses('manageGroup')"
            @click="submitGroup()">
            Submit
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  // actions
  GET_GROUPS,
  MANAGE_GROUP,
  DELETE_GROUP,

  // mutations
  SELECT_GROUP,
  UNSELECT_GROUP,
  CLEAR_GROUPS,
  CLEAR_MANAGE_GROUP_ERRORS,
} from '@/core/services/store/groups.module'
import { mapGetters } from 'vuex'
import { eventBus } from '@/main'
import {
  showToast,
  showModal,
  hideModal,
  showDeletePopover,
  hideDeletePopover,
} from '@/core/utils'

export default {
  name: 'SettingsGroups',
  data() {
    return {
      fields: [
        {
          label: '#',
          key: 'order',
          class: ['align-middle', 'pl-5'],
          thStyle: 'width: 50px;'
        },
        {
          label: 'Name',
          key: 'name',
          class: ['align-middle', 'font-weight-bold'],
        },
        {
          label: 'Code',
          key: 'code',
          class: ['align-middle'],
        },
        {
          label: 'Total Tariffs',
          key: 'tariff.count',
          class: ['align-middle', 'text-left text-md-right'],
        },
        {
          label: 'Active Servers',
          key: 'servers',
          class: ['align-middle', 'text-left text-md-right'],
        },
        {
          label: 'Actions',
          key: 'edit',
          class: ['align-middle', 'text-md-right', 'text-nowrap', 'pr-5', 'pl-10'],
        }
      ],

      showToast,
      showModal,
      hideModal,
      showDeletePopover,
      hideDeletePopover,
    }
  },
  computed: {
    ...mapGetters([
      'groupsList',
      'groupsEmpty',
      'totalGroups',
      'selectedGroup',
      'groupsModalTitle',
      'manageGroupErrors',
      'manageGroupError',
      'selectedGroupsExist',
      'groupsResponses',
    ]),
  },
  created() {
    this.$store.dispatch(GET_GROUPS)

    eventBus.$on('showToast/SettingsGroups', ({ message, variant }) => {
      this.showToast(message, variant)
    })
  },
  destroyed() {
    this.$store.commit(CLEAR_GROUPS)

    eventBus.$off('showToast/SettingsGroups')
  },
  methods: {
    submitGroup() {
      this.$store
        .dispatch(MANAGE_GROUP)
        .then(() => {
          this.hideModal('manageModal')
        })
    },

    selectGroup(group) {
      this.$store.commit(SELECT_GROUP, group)
    },

    unselectGroup() {
      this.$store.commit(UNSELECT_GROUP)
    },

    clearErrors() {
      this.$store.commit(CLEAR_MANAGE_GROUP_ERRORS)
    },

    getValidationState(field) {
      return this.manageGroupErrors.some(err => err.field == field) ? false : null
    },

    confirmDelete() {
      this.$store.dispatch(DELETE_GROUP)
    },
  }
}
</script>
